import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Badge from '@segment/matcha/components/Badge'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CardBase, * as Card from '@segment/matcha/recipes/Card'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Look under the hood`,
  description: `Having amazing data infrastructure used to be out of reach. Today, that’s no longer the case. We’ve solved the hard challenges around delivering billions of events reliably to downstream APIs so that you can focus on what matters — building an amazing product.`,
}

const cards = [
  {
    key: `Mobile-SDKs`,
    badge: `Mobile SDKs`,
    description: `Learn how our iOS and Android SDKs reduce your app’s power consumption by 2-3x.`,
    url: `/industry/mobile/`,
  },
  {
    key: `Deduplication`,
    badge: `Deduplication`,
    description: `Delivering billions of messages exactly once.`,
    url: `/product/protocols/`,
  },
  {
    key: `Reliability`,
    badge: `Reliability`,
    description: `Reliably sending billions of messages per day to hundreds of  public APIs.`,
    url: `https://segment.com/blog/introducing-centrifuge/`,
  },
  {
    key: `Functions`,
    badge: `Functions`,
    description: `Segment Functions allows you to deploy your own integrations with code.`,
    url: `/product/connections/functions/`,
  },
]

const LookUnderTheHood: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()

  return (
    <StyledSection variant="centered">
      <Section.Copy>
        <CopyTextBase centered={true}>
          <CopyText.Heading variant="h2" tag="h2">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description variant="pLarge">{copy.description}</CopyText.Description>
        </CopyTextBase>
      </Section.Copy>
      <Section.Contents>
        <Body>
          <ColumnTrack columns={1} overrides={{ md: 2 }}>
            {cards.map((card) => (
              <CardBase key={card.key} href={getAbsoluteUrl(card.url, pageConfig)}>
                <Card.Body>
                  <Card.Accessories>
                    <Card.AccessoriesRow>
                      <Badge variant="outlined">{card.badge}</Badge>
                    </Card.AccessoriesRow>
                  </Card.Accessories>
                  <Card.Copy>
                    <Card.Description variant="pLarge">{card.description}</Card.Description>
                  </Card.Copy>
                  <Card.Action alignment="right">
                    <ChevronRightIcon />
                  </Card.Action>
                </Card.Body>
              </CardBase>
            ))}
          </ColumnTrack>
        </Body>
      </Section.Contents>
    </StyledSection>
  )
}

const StyledSection = styled(SectionBase)`
  ${Section.Wrapper} {
    ${({ theme }) => theme.breakpoints.down(`md`)} {
      row-gap: 40px;
    }
  }
`

const Body = styled.div`
  max-width: 960px;
  margin: 0 auto;
`

export default LookUnderTheHood
